import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { api } from '@/api';
import { toast } from 'sonner';
import { 
  Mail, 
  Send, 
  Clock, 
  CheckCircle, 
  MessageSquare, 
  ExternalLink,
  ChevronDown,
  ChevronUp,
  User,
  Building2,
  RefreshCw
} from 'lucide-react';
import DistributionSelector from './DistributionSelector';

export default function EmailHistoryPanel({ projectId, documentType, documentId }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [responseDialogOpen, setResponseDialogOpen] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [responseNotes, setResponseNotes] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);

  useEffect(() => {
    loadHistory();
  }, [documentType, documentId]);

  const loadHistory = async () => {
    try {
      const response = await api.email.getHistoryForDocument(documentType, documentId);
      setHistory(response.data);
    } catch (error) {
      console.error('Failed to load email history');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateMailto = async (distribution) => {
    setGeneratingLink(true);
    try {
      const response = await api.email.generateMailto(projectId, documentType, documentId, distribution);
      const data = response.data;
      
      // Open mailto link using window.location which respects system default email client
      // This triggers the OS default mail handler (Outlook Classic, Thunderbird, etc.)
      window.location.href = data.mailto_link;
      
      toast.success(
        <div className="space-y-2">
          <p>Email opened in your default mail client</p>
          <p className="text-sm text-slate-600">Reference: {data.reference_number}</p>
          <p className="text-xs text-slate-500">Click "Mark as Sent" after sending</p>
        </div>
      );
      
      setSendDialogOpen(false);
      loadHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate email');
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleMarkSent = async (historyId) => {
    try {
      await api.email.markSent(historyId);
      toast.success('Email marked as sent');
      loadHistory();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleRecordResponse = async () => {
    if (!selectedHistory) return;
    
    try {
      await api.email.recordResponse(selectedHistory.id, responseNotes);
      toast.success('Response recorded');
      setResponseDialogOpen(false);
      setSelectedHistory(null);
      setResponseNotes('');
      loadHistory();
    } catch (error) {
      toast.error('Failed to record response');
    }
  };

  const getStatusBadge = (status, responseReceived) => {
    if (responseReceived) {
      return <Badge className="bg-green-100 text-green-700 border-green-200">Response Received</Badge>;
    }
    switch (status) {
      case 'sent':
        return <Badge className="bg-blue-100 text-blue-700 border-blue-200">Sent</Badge>;
      case 'draft':
        return <Badge className="bg-amber-100 text-amber-700 border-amber-200">Pending Send</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-600">{status}</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-barlow font-bold uppercase tracking-tight flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Email History
        </h3>
        <div className="flex gap-2">
          <Button
            onClick={() => loadHistory()}
            variant="outline"
            size="sm"
            className="rounded-sm"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button
            onClick={() => setSendDialogOpen(true)}
            data-testid="send-via-outlook-button"
            className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm font-medium uppercase tracking-wide text-xs px-4 py-2"
          >
            <Send className="h-4 w-4 mr-2" />
            Send via Email Client
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="p-4 text-slate-500 text-center">Loading email history...</div>
      ) : history.length === 0 ? (
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-8 text-center">
          <Mail className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600">No emails sent for this {documentType} yet.</p>
          <Button 
            onClick={() => setSendDialogOpen(true)} 
            variant="outline" 
            className="mt-4"
          >
            Send First Email
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {history.map((item) => (
            <div 
              key={item.id} 
              className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden"
            >
              <div 
                className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-sm font-bold text-slate-700">
                        {item.reference_number}
                      </span>
                      {getStatusBadge(item.status, item.response_received)}
                    </div>
                    <p className="text-sm font-medium text-slate-900 line-clamp-1">{item.subject}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Created: {formatDate(item.created_at)}
                      </span>
                      {item.sent_at && (
                        <span className="flex items-center gap-1">
                          <CheckCircle className="h-3 w-3 text-green-600" />
                          Sent: {formatDate(item.sent_at)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {item.status === 'draft' && (
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkSent(item.id);
                        }}
                        variant="outline"
                        size="sm"
                        className="text-xs"
                      >
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Mark Sent
                      </Button>
                    )}
                    {item.status === 'sent' && !item.response_received && (
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedHistory(item);
                          setResponseDialogOpen(true);
                        }}
                        variant="outline"
                        size="sm"
                        className="text-xs"
                      >
                        <MessageSquare className="h-3 w-3 mr-1" />
                        Record Response
                      </Button>
                    )}
                    {expandedId === item.id ? (
                      <ChevronUp className="h-4 w-4 text-slate-400" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-slate-400" />
                    )}
                  </div>
                </div>
              </div>

              {expandedId === item.id && (
                <div className="border-t border-slate-200 p-4 bg-slate-50">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Recipients (TO)</p>
                      <div className="space-y-1">
                        {item.recipient_details?.filter(r => item.recipients_to.includes(r.email)).map((r, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm">
                            {r.type === 'external' ? (
                              <Building2 className="h-3 w-3 text-slate-400" />
                            ) : (
                              <User className="h-3 w-3 text-slate-400" />
                            )}
                            <span>{r.name}</span>
                            <span className="text-slate-400">({r.discipline || r.role})</span>
                          </div>
                        ))}
                        {item.recipients_to?.filter(email => !item.recipient_details?.find(r => r.email === email)).map((email, i) => (
                          <div key={`extra-${i}`} className="text-sm text-slate-600">{email}</div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Recipients (CC)</p>
                      <div className="space-y-1">
                        {item.recipient_details?.filter(r => item.recipients_cc.includes(r.email)).map((r, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm">
                            {r.type === 'external' ? (
                              <Building2 className="h-3 w-3 text-slate-400" />
                            ) : (
                              <User className="h-3 w-3 text-slate-400" />
                            )}
                            <span>{r.name}</span>
                            <span className="text-slate-400">({r.discipline || r.role})</span>
                          </div>
                        ))}
                        {item.recipients_cc?.length === 0 && (
                          <span className="text-sm text-slate-400">None</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {item.attachment_names?.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Attachments</p>
                      <div className="flex flex-wrap gap-2">
                        {item.attachment_names.map((name, i) => (
                          <Badge key={i} variant="outline" className="text-xs">
                            {name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {item.response_received && item.response_notes && (
                    <div className="bg-green-50 border border-green-200 rounded-sm p-3">
                      <p className="text-xs font-bold uppercase tracking-wider text-green-700 mb-1">Response Notes</p>
                      <p className="text-sm text-green-800">{item.response_notes}</p>
                      <p className="text-xs text-green-600 mt-2">
                        Received: {formatDate(item.response_date)}
                      </p>
                    </div>
                  )}

                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Email Preview</p>
                    <pre className="text-xs text-slate-600 bg-white p-3 rounded border border-slate-200 whitespace-pre-wrap max-h-48 overflow-y-auto">
                      {item.body}
                    </pre>
                  </div>

                  <div className="mt-3 text-xs text-slate-400">
                    Created by {item.created_by_name}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Send via Outlook Dialog */}
      <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-barlow font-bold uppercase">
              Send via Email Client
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-sm p-3">
              <p className="text-sm text-blue-700">
                <strong>How it works:</strong>
              </p>
              <ol className="text-sm text-blue-700 mt-2 space-y-1 list-decimal list-inside">
                <li>Select your recipients below</li>
                <li>Click "Open in Outlook" to generate the email</li>
                <li>Review and send from your Outlook client</li>
                <li>Return here and click "Mark as Sent" to track it</li>
              </ol>
            </div>
            
            <DistributionSelector
              projectId={projectId}
              documentType={documentType}
              documentId={documentId}
              onSubmit={(result) => {
                // This is handled by handleGenerateMailto
              }}
              onCancel={() => setSendDialogOpen(false)}
              customSubmitButton={
                <Button
                  onClick={() => {
                    // Get the distribution from the selector state
                    const selector = document.querySelector('[data-distribution-selector]');
                    if (selector) {
                      const event = new CustomEvent('submitDistribution');
                      selector.dispatchEvent(event);
                    }
                  }}
                  disabled={generatingLink}
                  className="flex-1 bg-blue-600 text-white hover:bg-blue-700 rounded-sm font-medium uppercase tracking-wide text-xs px-6 py-3"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  {generatingLink ? 'Opening...' : 'Open in Outlook'}
                </Button>
              }
              onDistributionReady={handleGenerateMailto}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Record Response Dialog */}
      <Dialog open={responseDialogOpen} onOpenChange={setResponseDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-xl font-barlow font-bold uppercase">
              Record Response
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Record any response or feedback received for this email.
            </p>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                Response Notes
              </Label>
              <Textarea
                value={responseNotes}
                onChange={(e) => setResponseNotes(e.target.value)}
                placeholder="Enter response details, feedback, or follow-up actions..."
                rows={4}
                className="rounded-sm"
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  setResponseDialogOpen(false);
                  setSelectedHistory(null);
                  setResponseNotes('');
                }}
                variant="outline"
                className="flex-1 rounded-sm"
              >
                Cancel
              </Button>
              <Button
                onClick={handleRecordResponse}
                className="flex-1 bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
              >
                Save Response
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
